import { useEffect, useRef, useState } from 'react';

import { useFiatCurrency } from '@utils/hooks';

import { useStore } from '@modules';
import { AccountsBuyRateResponse, AccountsSellRateResponse } from '@modules/accounts/types';
import { GETBuyRateResponse } from '@modules/buy/types';
import { CurrencyEnum } from '@modules/global/types';
import { GETSellRateResponse } from '@modules/sell/types';
import { GETWithdrawRateResponse } from '@modules/withdraw/types';

import { useCustomContext } from '@containers/OperationBlock/context';

type Options = {
  fromCurrencies: CurrencyEnum[];
  toCurrencies: CurrencyEnum[];
  cacheState?: boolean;
};

export type RateType =
  | GETBuyRateResponse
  | GETSellRateResponse
  | GETWithdrawRateResponse
  | AccountsBuyRateResponse
  | AccountsSellRateResponse;

let cacheFromAmount = '';
let cacheFromCurrency: CurrencyEnum | undefined;
let cacheToAmount = '';
let cacheToCurrency: CurrencyEnum | undefined;

const useConverterState = (options: Options) => {
  const { left, right } = useCustomContext();
  const defaultFiatCurrency = useFiatCurrency();
  const [{ buy, sell, withdraw, accounts }] = useStore();
  const expiredRatesError = [
    buy.errors.code,
    sell.errors.code,
    withdraw.errors.code,
    accounts.errors.code,
  ].includes(CONST.errorCodes.rateExpired);
  const [updateRatesFlag, setUpdateRatesFlag] = useState(false);
  const resolveUpdateRates = useRef<() => void>(null);

  const [fromAmount, setFromAmount] = useState(options.cacheState ? cacheFromAmount : '');
  const defaultFromCurrency =
    (options.cacheState && cacheFromCurrency) ||
    options.fromCurrencies.find(c => c === defaultFiatCurrency) ||
    options.fromCurrencies[0];
  const [fromCurrency, setFromCurrency] = useState(defaultFromCurrency);
  const [fromLimit, setFromLimit] = useState('');

  const [toAmount, setToAmount] = useState(options.cacheState ? cacheToAmount : '');
  const defaultToCurrency =
    (options.cacheState && cacheToCurrency) ||
    options.toCurrencies.find(c => c === defaultFiatCurrency) ||
    options.toCurrencies[0];
  const [toCurrency, setToCurrency] = useState(defaultToCurrency);
  const [toLimit, setToLimit] = useState('');

  const [rate, setRate] = useState<RateType>();
  const [focusedCurrency, setFocusedCurrency] = useState(fromCurrency);
  const [showLimits, setShowLimits] = useState(false);
  const defaultInputCurrency = fromAmount ? fromCurrency : toAmount ? toCurrency : fromCurrency;
  const [inputCurrency, setInputCurrency] = useState(defaultInputCurrency);

  const updateRates = () =>
    new Promise<void>(resolve => {
      resolveUpdateRates.current = resolve;
      setUpdateRatesFlag(prev => !prev);
    });

  useEffect(() => {
    if (rate) resolveUpdateRates.current?.();
  }, [rate]);

  useEffect(() => {
    const inputFrom = inputCurrency === fromCurrency;
    cacheFromAmount = inputFrom ? fromAmount : '';
    cacheFromCurrency = inputFrom ? fromCurrency : undefined;
    cacheToAmount = !inputFrom ? toAmount : '';
    cacheToCurrency = !inputFrom ? toCurrency : undefined;
    if (!fromAmount && !toAmount) setRate(undefined);
  }, [fromAmount, fromCurrency, toAmount, toCurrency]);

  useEffect(() => {
    if (options.fromCurrencies.length > 0 && !options.fromCurrencies.includes(fromCurrency))
      setFromCurrency(options.fromCurrencies[0]);
  }, [options.fromCurrencies]);

  useEffect(() => {
    if (options.toCurrencies.length > 0 && !options.toCurrencies.includes(toCurrency))
      setToCurrency(options.toCurrencies[0]);
  }, [options.toCurrencies]);

  useEffect(() => {
    if (options.fromCurrencies.includes(left?.value as CurrencyEnum))
      setFromCurrency(left?.value as CurrencyEnum);
  }, [left?.value]);

  useEffect(() => {
    if (options.toCurrencies.includes(right?.value as CurrencyEnum))
      setToCurrency(right?.value as CurrencyEnum);
  }, [right?.value]);

  return {
    options,
    fromAmount,
    setFromAmount,
    fromCurrency,
    setFromCurrency,
    toAmount,
    setToAmount,
    toCurrency,
    setToCurrency,
    rate,
    setRate,
    focusedCurrency,
    setFocusedCurrency,
    showLimits,
    setShowLimits,
    fromLimit,
    setFromLimit,
    toLimit,
    setToLimit,
    inputCurrency,
    setInputCurrency,
    updateRates,
    paramsRequireRateUpdate: [toCurrency, fromCurrency, expiredRatesError, updateRatesFlag],
  };
};

export default useConverterState;
